import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Lenis from 'lenis';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Background from './components/Background';
import CustomCursor from './components/CustomCursor';
import PageLoader from './components/PageLoader';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';

const Landing = lazy(() => import('./pages/Landing'));
const Blog = lazy(() => import('./sections/Blog'));
const NotFound = lazy(() => import('./sections/NotFound'));

function SmoothScroll({ children }) {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== '/') return undefined;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, [location.pathname]);

  return children;
}

function AppContent() {
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : true;
  });
  const [showLoader, setShowLoader] = useState(
    () => location.pathname === '/' && !sessionStorage.getItem('portfolio-loaded')
  );

  useEffect(() => {
    document.body.classList.toggle('dark-mode', darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const handleLoaderComplete = () => {
    sessionStorage.setItem('portfolio-loaded', '1');
    setShowLoader(false);
  };

  return (
    <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
      {showLoader && <PageLoader onComplete={handleLoaderComplete} />}
      <Background />
      <CustomCursor />
      <div className="app__content">
        <Navbar darkMode={darkMode} setDarkMode={setDarkMode} />
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Footer />
        <ScrollToTop />
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <SmoothScroll>
          <AppContent />
        </SmoothScroll>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
